import React, { useState } from "react";
import { Box, Typography, IconButton, Popover, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";

import MessageList from "./components/MessageList";

import { screenWrapperSx } from "./MessagePreview.styles";

import Background from "../../../assets/message-preview/background.png";
import { radii } from "../../../theme/shape";

const getEmailPreviewContent = (messages = []) => {
  const firstFilledMessage =
    messages.find((message) => {
      const value = message?.content ?? message?.html ?? "";
      return String(value).trim().length > 0;
    }) || messages?.[0] || {};
  const rawContent = firstFilledMessage?.content || firstFilledMessage?.html || "";
  const normalizedContent = String(rawContent).trim();
  const hasHtmlMarkup = /<\/?[a-z][\s\S]*>/i.test(normalizedContent);
  const emailImageUrl = firstFilledMessage?.header?.type === "img"
    ? firstFilledMessage?.header?.content
    : "";
  const isImageUploading = Boolean(firstFilledMessage?.header?.uploading);
  const savedEmailSubtitle = String(firstFilledMessage?.email_subtitle || "").trim();
  const savedEmailAccentColor = String(firstFilledMessage?.email_accent_color || "").trim();

  return {
    messageId: firstFilledMessage?.id || "auto-reply",
    content: rawContent,
    isHtml: hasHtmlMarkup,
    imageUrl: emailImageUrl,
    isImageUploading,
    savedEmailSubtitle,
    savedEmailAccentColor,
  };
};

const MessagePreview = ({
  channel,
  messages = [],
  topInset = 0,
  emailHeaderText = "",
  emailAccentColor = "",
  emailAccentOptions = [],
  onEmailAccentColorChange,
  onButtonsChange,
  readOnly = false,
  hideMediaActions = false,
  highlightIncoming = false,
  onPickImage,
  onPickPdf,
  onReplaceMedia,
  onRemoveMedia,
  allowedButtonTypes,
  pixDefaults,
}) => {
  const theme = useTheme();
  const previewChannel = channel;
  const isTemplateMobileLayout = ["whatsapp_api", "sms"].includes(previewChannel);
  const isSms = previewChannel === "sms";
  const [colorAnchorEl, setColorAnchorEl] = useState(null);

  if (previewChannel === "email") {
    const {
      messageId,
      content,
      isHtml,
      imageUrl,
      isImageUploading,
      savedEmailSubtitle,
      savedEmailAccentColor,
    } = getEmailPreviewContent(messages);
    const canPickEmailImage = Boolean(onPickImage) && !readOnly;
    const canPickAccentColor = Boolean(onEmailAccentColorChange) && !readOnly;
    const headerText = emailHeaderText || savedEmailSubtitle || "";
    const resolvedAccentColor = emailAccentColor || savedEmailAccentColor || theme.palette.primary.main;
    const introText = content || "";
    const isColorPopoverOpen = Boolean(colorAnchorEl);
    const normalizedAccent = String(resolvedAccentColor || "").toUpperCase();
    const normalizedWhite = theme.palette.common.white.toUpperCase();
    const isLightAccent = normalizedAccent === normalizedWhite || normalizedAccent === "WHITE";
    const headerTextColor = isLightAccent
      ? theme.palette.text.primary
      : theme.palette.primary.contrastText;
    const logoPlaceholderBorder = isLightAccent
      ? `2px dashed ${alpha(theme.palette.text.primary, 0.35)}`
      : `2px dashed ${alpha(theme.palette.primary.contrastText, 0.65)}`;
    const logoPlaceholderHover = isLightAccent
      ? alpha(theme.palette.text.primary, 0.06)
      : alpha(theme.palette.primary.contrastText, 0.08);

    return (
      <Box
        sx={{
          ...screenWrapperSx,
          display: "flex",
          flexDirection: "column",
          textAlign: "left",
          alignItems: "stretch",
          justifyContent: "flex-start",
          minHeight: 0,
          p: 1,
          pt: topInset ? `${topInset + 8}px` : 1,
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            flex: 1,
            minHeight: 0,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: radii.base,
            overflowY: isHtml ? "hidden" : "auto",
            overflowX: "hidden",
            backgroundColor: "background.paper",
          }}
        >
          {isHtml ? (
            <Box
              component="iframe"
              title="Email HTML Preview"
              srcDoc={content}
              sandbox="allow-popups allow-popups-to-escape-sandbox"
              sx={{
                backgroundColor: "background.paper",
                border: 0,
                display: "block",
                height: "100%",
                width: "100%",
              }}
            />
          ) : (
            <Box
              sx={{
                backgroundColor: "background.paper",
                height: "100%",
                minHeight: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box sx={{ backgroundColor: resolvedAccentColor, p: 3, textAlign: "center", position: "relative" }}>
                {canPickAccentColor && (
                  <>
                    <IconButton
                      size="small"
                      onClick={(event) => setColorAnchorEl(event.currentTarget)}
                      sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        backgroundColor: alpha(theme.palette.background.paper, 0.9),
                        "&:hover": { backgroundColor: "background.paper" },
                      }}
                    >
                      <PaletteOutlinedIcon
                        sx={{ color: isLightAccent ? "text.primary" : resolvedAccentColor, fontSize: 19 }}
                      />
                    </IconButton>
                    <Popover
                      open={isColorPopoverOpen}
                      anchorEl={colorAnchorEl}
                      onClose={() => setColorAnchorEl(null)}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                      <Box sx={{ p: 1.2, display: "flex", alignItems: "center", gap: 0.8 }}>
                        {emailAccentOptions.map((color) => {
                          const isActive = color === resolvedAccentColor;
                          const isWhite = String(color).toUpperCase() === normalizedWhite;
                          return (
                            <Box
                              key={color}
                              component="button"
                              type="button"
                              onClick={() => {
                                onEmailAccentColorChange(color);
                                setColorAnchorEl(null);
                              }}
                              aria-label={`Selecionar cor ${color}`}
                              sx={{
                                width: 30,
                                height: 30,
                                borderRadius: radii.base,
                                border: isActive ? "2px solid" : "1px solid",
                                borderColor: isActive
                                  ? "text.primary"
                                  : isWhite
                                    ? "border.strong"
                                    : "background.paper",
                                boxShadow: isActive
                                  ? `0 0 0 2px ${theme.palette.border.default}`
                                  : "none",
                                backgroundColor: color,
                                cursor: "pointer",
                              }}
                            />
                          );
                        })}
                      </Box>
                    </Popover>
                  </>
                )}
                {imageUrl ? (
                  <Box
                    component="img"
                    src={imageUrl}
                    alt="Logo da empresa"
                    onClick={() => canPickEmailImage && onPickImage(messageId)}
                    sx={{
                      maxWidth: 220,
                      width: "100%",
                      height: "auto",
                      cursor: canPickEmailImage ? "pointer" : "default",
                      borderRadius: radii.base,
                      border: canPickEmailImage ? logoPlaceholderBorder : "none",
                      p: canPickEmailImage ? 0.7 : 0,
                      transition: "opacity 0.2s ease",
                      "&:hover": canPickEmailImage
                        ? { opacity: 0.9 }
                        : {},
                    }}
                  />
                ) : (
                  <Box
                    onClick={() => canPickEmailImage && onPickImage(messageId)}
                    sx={{
                      width: 140,
                      height: 140,
                      mx: "auto",
                      borderRadius: radii.base,
                      border: logoPlaceholderBorder,
                      color: headerTextColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13,
                      fontWeight: 600,
                      px: 1.5,
                      cursor: canPickEmailImage ? "pointer" : "default",
                      transition: "background-color 0.2s ease",
                      "&:hover": canPickEmailImage
                        ? { backgroundColor: logoPlaceholderHover }
                        : {},
                    }}
                  >
                    Sua logo aqui
                  </Box>
                )}
                {canPickEmailImage && Boolean(imageUrl) && (
                  <Typography sx={{ color: headerTextColor, fontSize: 12, mt: 1 }}>
                    Clique na imagem para alterar
                  </Typography>
                )}
                {isImageUploading && (
                  <Typography sx={{ color: headerTextColor, fontSize: 12, mt: 0.5 }}>
                    Enviando imagem...
                  </Typography>
                )}
                {!!headerText && (
                  <Typography
                    sx={{
                      color: headerTextColor,
                      fontSize: 30,
                      fontWeight: 700,
                      mt: 2,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      maxWidth: "100%",
                    }}
                  >
                    {headerText}
                  </Typography>
                )}
              </Box>

              <Box sx={{ backgroundColor: "background.paper", p: 3, flex: 1 }}>
                {Boolean(introText.trim()) && (
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: 1.6,
                      color: "text.primary",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      maxWidth: "100%",
                      mb: 2,
                    }}
                  >
                    {introText}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  if (isTemplateMobileLayout) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          borderRadius: radii.md,
          ...(isSms
            ? {
                backgroundColor: "surface.muted",
              }
            : {
                backgroundColor: "surface.subtle",
                backgroundImage: `url(${Background}), url(${Background}), url(${Background}), linear-gradient(${theme.palette.border.default}, ${theme.palette.border.default})`,
                backgroundRepeat: "repeat, repeat, repeat, no-repeat",
                backgroundPosition: "top left, top left, top left, center",
                backgroundSize: "280px auto, 280px auto, 280px auto, cover",
                backgroundBlendMode: "multiply, multiply, multiply, normal",
              }),
          p: 1.25,
          pt: topInset ? `${topInset + 10}px` : 1.25,
          border: "1px solid",
          borderColor: isSms ? "divider" : "border.default",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <MessageList
          messages={messages}
          onButtonsChange={onButtonsChange}
          channel={previewChannel}
          readOnly={readOnly}
          hideMediaActions={hideMediaActions}
          highlightIncoming={highlightIncoming}
          onPickImage={onPickImage}
          onPickPdf={onPickPdf}
          onReplaceMedia={onReplaceMedia}
          onRemoveMedia={onRemoveMedia}
          allowedButtonTypes={allowedButtonTypes}
          pixDefaults={pixDefaults}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        borderRadius: radii.sm,
        backgroundImage: `url(${Background})`,
        backgroundColor: "background.paper",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        display: "flex",
        flexDirection: "column",
        pt: topInset ? `${topInset}px` : 0,
      }}
    >
      <MessageList
        messages={messages}
        onButtonsChange={onButtonsChange}
        channel={previewChannel}
        readOnly={readOnly}
        hideMediaActions={hideMediaActions}
        highlightIncoming={highlightIncoming}
        onPickImage={onPickImage}
        onPickPdf={onPickPdf}
        onReplaceMedia={onReplaceMedia}
        onRemoveMedia={onRemoveMedia}
        allowedButtonTypes={allowedButtonTypes}
      />
    </Box>
  );
};

export default MessagePreview;
